// @flow

import * as React from 'react';
import {defineMessages, useIntl} from 'react-intl';

import {Dialog, DialogHeader, DialogContent} from 'sail/Dialog';
import {CaptionAlt, Text} from 'sail/Text';
import Group from 'sail/Group';
import ModalLayer from 'sail/ModalLayer';
import {getUserOS} from 'docs/util';
import CluiButtonLink from './ButtonLink';
import {KEY_MAPPINGS as K} from '../helpers/cli/key_mappings';

const messages = defineMessages({
  tipLabel: {
    id: 'docsApp.stripeShellShortcut.tipLabel',
    description:
      'Tooltip label of the dialog which displays keyboard shortcuts.',
    defaultMessage: 'Shortcuts',
  },
  title: {
    id: 'docsApp.stripeShellShortcut.title',
    description: 'Title of the dialog which displays keyboard shortcuts.',
    defaultMessage: 'Keyboard shortcuts',
  },
  close: {
    id: 'docsApp.stripeShellShortcut.close',
    description: 'Label for the action to close the dialog',
    defaultMessage: 'Close',
  },
  toggleShell: {
    id: 'docsApp.stripeShellShortcut.toggleShell',
    description:
      'Description of the key combination to toggle the Stripe Shell open',
    defaultMessage: 'Expand / Minimize the Stripe Shell',
  },
  nextPreviousHistory: {
    id: 'docsApp.stripeShellShortcut.nextPreviousHistory',
    description:
      'Description of the key combination to show previous or next command history.',
    defaultMessage: 'Next / Previous history',
  },
  highlightSuggestion: {
    id: 'docsApp.stripeShellShortcut.highlightSuggestion',
    description:
      'Description of the key combination to highlight a suggestion.',
    defaultMessage: 'Highlight next / previous suggestion',
  },
  copy: {
    id: 'docsApp.stripeShellShortcut.copy',
    description: 'Description of the key combination to copy.',
    defaultMessage: 'Copy',
  },
  paste: {
    id: 'docsApp.stripeShellShortcut.paste',
    description: 'Description of the key combination to paste.',
    defaultMessage: 'Paste',
  },
  clearPrompt: {
    id: 'docsApp.stripeShellShortcut.clearPrompt',
    description: 'Description of the key combination to clear the prompt.',
    defaultMessage: 'Clear the prompt',
  },
  deletePreviousWord: {
    id: 'docsApp.stripeShellShortcut.deletePreviousWord',
    description:
      'Description of the key combination to delete the previous word.',
    defaultMessage: 'Delete previous word',
  },
  clearTerminalOutput: {
    id: 'docsApp.stripeShellShortcut.clearTerminalOutput',
    description:
      'Description of the key combination to clear the terminal output.',
    defaultMessage: 'Clear terminal output',
  },
  cursorToBeginning: {
    id: 'docsApp.stripeShellShortcut.cursorToBeginning',
    description:
      'Description of the key combination to move the cursor to the beginning.',
    defaultMessage: 'Move cursor to the beginning of the prompt',
  },
  cursorToEnd: {
    id: 'docsApp.stripeShellShortcut.cursorToEnd',
    description:
      'Description of the key combination to move the cursor to the end.',
    defaultMessage: 'Move cursor to the end of the prompt',
  },
  focusPreviousPane: {
    id: 'docsApp.stripeShellShortcut.focusPreviousPane',
    description:
      'Description of the key combination to focus the previous pane.',
    defaultMessage: 'Focus previous pane',
  },
  focusNextPane: {
    id: 'docsApp.stripeShellShortcut.focusNextPane',
    description: 'Description of the key combination to focus the next pane.',
    defaultMessage: 'Focus next pane',
  },
  addPane: {
    id: 'docsApp.stripeShellShortcut.addPane',
    description: 'Description of the key combination to add a new pane.',
    defaultMessage: 'Add new pane',
  },
});

const DOCUMENTED_KEYS = [
  [[{key: '`', modifiers: ['ctrlKey']}], messages.toggleShell],
  [[K.ARROW_DOWN, K.ARROW_UP], messages.nextPreviousHistory],
  [[K.TAB, K.SHIFT_TAB], messages.highlightSuggestion],
  [[K.CMD_C, K.CTRL_SHIFT_C], messages.copy],
  [[K.CMD_V, K.CTRL_V], messages.paste],
  [[K.CTRL_U], messages.clearPrompt],
  [[K.CTRL_W, K.ALT_BACKSPACE], messages.deletePreviousWord],
  [[K.CMD_K, K.CTRL_L], messages.clearTerminalOutput],
  [[K.CTRL_A, K.CMD_ARROW_LEFT, K.CTRL_ARROW_LEFT], messages.cursorToBeginning],
  [[K.CTRL_E, K.CMD_ARROW_RIGHT, K.CTRL_ARROW_RIGHT], messages.cursorToEnd],
  [[K.CMD_LEFT_BRACKET, K.CTRL_LEFT_BRACKET], messages.focusPreviousPane],
  [[K.CMD_RIGHT_BRACKET, K.CTRL_RIGHT_BRACKET], messages.focusNextPane],
  [[K.CMD_D, K.ALT_SHIFT_D], messages.addPane],
];

const getOSModifierLabels = () => {
  const userOS = getUserOS();

  if (userOS === 'mac') {
    return {
      metaKey: '⌘',
      altKey: '⌥',
    };
  } else if (userOS === 'windows') {
    return {
      metaKey: 'Win',
      altKey: '⎇',
    };
  } else {
    return {
      metaKey: 'Meta',
      altKey: 'Alt',
    };
  }
};

const mappingToString = (mapping) => {
  const modifiers = mapping?.modifiers ?? [];
  const modifierLabels = getOSModifierLabels();

  const cmd = modifiers.includes('metaKey') ? modifierLabels.metaKey : '';
  const ctrl = modifiers.includes('ctrlKey') ? '⌃' : '';
  const shift = modifiers.includes('shiftKey') ? '⇧' : '';
  const alt = modifiers.includes('altKey') ? modifierLabels.altKey : '';

  let key = mapping?.key ?? '';
  if (key.length === 1) {
    key = key.toUpperCase();
  }

  return [cmd, ctrl, alt, shift, key].filter(Boolean).join('+');
};

const KeyboardShortcuts = () => {
  const {formatMessage} = useIntl();
  const [isVisible, setVisible] = React.useState(false);

  return (
    <>
      <CluiButtonLink
        icon="help"
        tip={formatMessage(messages.tipLabel)}
        onClick={() => setVisible(true)}
      />
      <ModalLayer
        shown={isVisible}
        onWashClick={() => setVisible(false)}
        onEscape={() => setVisible(false)}
      >
        <Dialog width="large">
          <DialogHeader
            title={formatMessage(messages.title)}
            closeLabel={formatMessage(messages.close)}
            onClose={() => setVisible(false)}
          />
          <DialogContent padding={20}>
            <Group direction="vertical" spacing={8} alignItems="flex-start">
              {DOCUMENTED_KEYS.map(([keyCombos, description], idx) => (
                <Group
                  direction="horizontal"
                  spacing={8}
                  alignItems="baseline"
                  key={idx}
                >
                  <Group
                    direction="horizontal"
                    spacing={4}
                    alignItems="baseline"
                  >
                    {keyCombos.map((combo, keyComboIdx) => (
                      <CaptionAlt
                        color="gray700"
                        style={{
                          padding: '0 6px',
                          background: 'var(--sail-color-gray-100)',
                          height: 20,
                          lineHeight: '20px',
                          borderRadius: 3,
                          display: 'inline-block',
                        }}
                        key={keyComboIdx}
                      >
                        {mappingToString(combo)}
                      </CaptionAlt>
                    ))}
                  </Group>
                  <Text
                    style={{
                      color: 'var(--gray500)',
                      display: 'inline',
                      verticalAlign: 'middle',
                      lineHeight: 0,
                    }}
                  >
                    {formatMessage(description)}
                  </Text>
                </Group>
              ))}
            </Group>
          </DialogContent>
        </Dialog>
      </ModalLayer>
    </>
  );
};

export default KeyboardShortcuts;
